// frontend/src/components/admin/FeatureManageModal.jsx
// ============================================================================
// MODAL GESTION FEATURE PAR ENTREPRISE — THEME-AWARE + EXTENSIONS
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Search, CheckCircle2, XCircle, RefreshCw, Building2,
  Calendar, User, ToggleLeft, ToggleRight, Zap, DollarSign,
  AlertCircle, Edit3, Check
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

// ============================================================================
// SOUS-COMPOSANT : Champ prix inline
// ============================================================================
const PrixField = ({ value, onChange, onSave, saving, devise = 'FCFA', t }) => {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value || '');

  const handleSave = async () => {
    await onSave(parseFloat(local) || 0);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 mt-2">
        <input
          type="number"
          min="0"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          autoFocus
          className="w-28 px-2 py-1 rounded-lg text-[12px] font-bold border outline-none"
          style={{ backgroundColor: t.bgInput, borderColor: t.borderActive, color: t.text }}
        />
        <span className="text-[11px] font-bold" style={{ color: t.textMuted }}>{devise}</span>
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-6 h-6 rounded flex items-center justify-center transition-colors"
          style={{ backgroundColor: t.successText + '20', color: t.successText }}
        >
          {saving ? <RefreshCw size={11} className="animate-spin" /> : <Check size={11} />}
        </button>
        <button
          onClick={() => { setLocal(value || ''); setEditing(false); }}
          className="w-6 h-6 rounded flex items-center justify-center transition-colors"
          style={{ backgroundColor: t.primarySofter, color: t.textMuted }}
        >
          <X size={11} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="flex items-center gap-1 mt-2 px-2 py-1 rounded-lg border transition-all duration-200 group"
      style={{ backgroundColor: t.warningText + '10', borderColor: t.warningText + '30', color: t.warningText }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = t.warningText + '60'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = t.warningText + '30'}
    >
      <DollarSign size={11} />
      <span className="text-[12px] font-bold tabular-nums">
        {value ? `${Number(value).toLocaleString('fr-FR')} ${devise}` : 'Saisir le prix'}
      </span>
      <Edit3 size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

// ============================================================================
// SOUS-COMPOSANTS LIGNES
// ============================================================================

const EnabledRow = ({ company, feature, t, processing, prixSaving, onToggle, onSavePrice }) => {
  const isExtension = feature?.is_extension === 1;

  return (
    <div
      className="flex items-start justify-between px-4 py-3 rounded-xl border transition-all duration-200"
      style={{ backgroundColor: t.successSoft, borderColor: t.successText + '20' }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = t.successText + '40'}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = t.successText + '20'}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: t.successText + '15' }}>
          <Building2 size={14} style={{ color: t.successText }} />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-bold truncate" style={{ color: t.text }}>
            {company.company_name}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] font-mono" style={{ color: t.textMuted }}>{company.company_slug}</span>
            {company.enabled_at && (
              <>
                <span style={{ color: t.textMuted }}>·</span>
                <span className="text-[11px] flex items-center gap-1" style={{ color: t.textMuted }}>
                  <Calendar size={10} />
                  {new Date(company.enabled_at).toLocaleDateString('fr-FR')}
                  {company.enabled_by_name && (
                    <span className="flex items-center gap-0.5 ml-1">
                      <User size={10} /> {company.enabled_by_name}
                    </span>
                  )}
                </span>
              </>
            )}
          </div>

          {/* Prix si extension */}
          {isExtension && (
            <PrixField
              value={company.prix_mensuel}
              onChange={() => {}}
              onSave={(prix) => onSavePrice(company, prix)}
              saving={prixSaving === company.id}
              t={t}
            />
          )}

          {/* Badge "inclus dans licence" si flag de licence */}
         {!isExtension && company.licence_has_flag === 1 && (
            <div className="flex items-center gap-1 mt-1.5">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
                style={{ backgroundColor: t.primary + '15', color: t.primaryText }}>
                <AlertCircle size={9} />
                Inclus dans la licence {company.plan_type}
              </span>
            </div>
          )}
        </div>
      </div>

      <button onClick={() => onToggle(company)} disabled={processing === company.id}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold border transition-all duration-200 disabled:opacity-40 active:scale-[0.97] flex-shrink-0 ml-3"
        style={{ backgroundColor: t.dangerSoft, borderColor: t.dangerText + '25', color: t.dangerText }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.danger; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = t.danger; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.dangerSoft; e.currentTarget.style.color = t.dangerText; e.currentTarget.style.borderColor = t.dangerText + '25'; }}
      >
        {processing === company.id ? <RefreshCw size={13} className="animate-spin" /> : <ToggleRight size={13} />}
        Désactiver
      </button>
    </div>
  );
};

const DisabledRow = ({ company, feature, t, processing, onToggle }) => {
  const isExtension = feature?.is_extension === 1;

  // Vérifier si ce flag est déjà inclus dans la licence de l'entreprise
  const includedInLicence = !isExtension && company.licence_has_flag;

  return (
    <div
      className="flex items-start justify-between px-4 py-3 rounded-xl border transition-all duration-200"
      style={{ backgroundColor: t.bgCard, borderColor: t.border }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.bgCardHover; e.currentTarget.style.borderColor = t.borderActive + '40'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.bgCard; e.currentTarget.style.borderColor = t.border; }}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: t.primarySofter }}>
          <Building2 size={14} style={{ color: t.textMuted }} />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-bold truncate" style={{ color: t.text }}>
            {company.company_name}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[11px] font-mono" style={{ color: t.textMuted }}>{company.company_slug}</span>
            {company.plan_type && (
              <span className="text-[11px] px-1.5 py-0.5 rounded font-semibold"
                style={{ backgroundColor: t.primarySofter, color: t.textMuted }}>
                {company.plan_type}
              </span>
            )}
          </div>

          {/* Avertissement si déjà inclus dans licence */}
          {includedInLicence && (
            <div className="flex items-center gap-1 mt-1.5">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold"
                style={{ backgroundColor: t.warningText + '15', color: t.warningText }}>
                <AlertCircle size={9} />
                Déjà inclus via la licence — activation redondante
              </span>
            </div>
          )}
        </div>
      </div>

      <button onClick={() => onToggle(company)} disabled={processing === company.id}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold border transition-all duration-200 disabled:opacity-40 active:scale-[0.97] flex-shrink-0 ml-3"
        style={{ backgroundColor: t.successSoft, borderColor: t.successText + '25', color: t.successText }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = t.success; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = t.success; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = t.successSoft; e.currentTarget.style.color = t.successText; e.currentTarget.style.borderColor = t.successText + '25'; }}
      >
        {processing === company.id ? <RefreshCw size={13} className="animate-spin" /> : <ToggleLeft size={13} />}
        {isExtension ? 'Activer + Prix' : 'Activer'}
      </button>
    </div>
  );
};

const GroupHeader = ({ icon: Icon, label, count, color, t }) => (
  <div className="flex items-center gap-2 mb-3 mt-1">
    <Icon size={14} style={{ color }} />
    <span className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: t.textMuted }}>
      {label}
    </span>
    <span className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded"
      style={{ backgroundColor: color + '12', color }}>
      {count}
    </span>
  </div>
);

// ============================================================================
// MODAL PRIX — s'ouvre quand on active une extension
// ============================================================================
const PrixActivationModal = ({ company, feature, onConfirm, onCancel, t }) => {
  const [prix, setPrix] = useState(feature?.prix_defaut || '');

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[60] p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-2xl w-full max-w-sm border p-6"
        style={{ backgroundColor: t.bgCard, borderColor: t.border, boxShadow: t.shadowLg }}>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: t.warningText + '15' }}>
            <Zap size={18} style={{ color: t.warningText }} />
          </div>
          <div>
            <h3 className="text-[15px] font-bold" style={{ color: t.text }}>
              Activer l'extension
            </h3>
            <p className="text-[12px]" style={{ color: t.textMuted }}>
              {feature?.feature_label || feature?.feature_name}
            </p>
          </div>
        </div>

        <p className="text-[13px] font-medium mb-4" style={{ color: t.textSecondary }}>
          Définir le prix mensuel pour <strong style={{ color: t.text }}>{company.company_name}</strong>
        </p>

        <div className="mb-5">
          <label className="text-[11px] font-bold uppercase tracking-wider mb-1.5 block"
            style={{ color: t.textMuted }}>
            Prix mensuel (FCFA)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              value={prix}
              onChange={(e) => setPrix(e.target.value)}
              placeholder={feature?.prix_defaut ? `Défaut : ${feature.prix_defaut}` : '0'}
              autoFocus
              className="flex-1 px-3 py-2.5 rounded-lg text-[13px] font-bold border outline-none transition-all"
              style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.text }}
              onFocus={(e) => { e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
              onBlur={(e) => { e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
            />
            <span className="text-[12px] font-bold" style={{ color: t.textMuted }}>FCFA/mois</span>
          </div>
          {feature?.prix_defaut > 0 && (
            <p className="text-[11px] mt-1.5" style={{ color: t.textMuted }}>
              Prix de référence : {Number(feature.prix_defaut).toLocaleString('fr-FR')} FCFA
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <button onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-[13px] font-bold border transition-all"
            style={{ backgroundColor: t.primarySofter, borderColor: t.border, color: t.textSecondary }}>
            Annuler
          </button>
          <button onClick={() => onConfirm(parseFloat(prix) || 0)}
            className="flex-1 py-2.5 rounded-xl text-[13px] font-bold border transition-all"
            style={{ backgroundColor: t.warningText, borderColor: t.warningText, color: '#fff' }}>
            Activer
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const FeatureManageModal = ({ feature, onClose, onUpdate }) => {
  const t = useTheme();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [processing, setProcessing] = useState(null);
  const [prixSaving, setPrixSaving] = useState(null);
  const [prixModal, setPrixModal] = useState(null); // company en attente d'activation extension

  const isExtension = feature?.is_extension === 1;

  // ── Load ──
  useEffect(() => { loadCompanies(); }, [feature]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await superAdminApi.features.getCompanies(feature.feature_name);
      setCompanies(response.data || []);
    } catch (error) {
      console.error('[FeatureManage] Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Toggle ──
  const handleToggle = async (company) => {
    const isEnabled = company.is_enabled === 1;

    // Si c'est une extension qu'on active → ouvrir modal prix
    if (isExtension && !isEnabled) {
      setPrixModal(company);
      return;
    }

    setProcessing(company.id);
    try {
      if (isEnabled) {
        // Désactivation
        if (isExtension) {
          await superAdminApi.extensions.deactivate(company.id, feature.feature_name);
        } else {
          await superAdminApi.features.deactivate(feature.feature_name, company.id);
        }
      } else {
        await superAdminApi.features.activate(feature.feature_name, company.id);
      }
      await loadCompanies();
      onUpdate();
    } catch (error) {
      console.error('[FeatureManage] Toggle error:', error);
    } finally {
      setProcessing(null);
    }
  };

  // ── Confirmer activation extension avec prix ──
  const handleConfirmExtension = async (prix) => {
    const company = prixModal;
    setPrixModal(null);
    setProcessing(company.id);
    try {
      await superAdminApi.extensions.activate(company.id, feature.feature_name, prix);
      await loadCompanies();
      onUpdate();
    } catch (error) {
      console.error('[FeatureManage] Extension activate error:', error);
    } finally {
      setProcessing(null);
    }
  };

  // ── Sauvegarder prix ──
  const handleSavePrice = async (company, prix) => {
    setPrixSaving(company.id);
    try {
      await superAdminApi.extensions.updatePrice(company.id, feature.feature_name, prix);
      await loadCompanies();
    } catch (error) {
      console.error('[FeatureManage] Price save error:', error);
    } finally {
      setPrixSaving(null);
    }
  };

  // ── Filter ──
  const filtered = useMemo(() => {
    if (!searchTerm) return companies;
    const q = searchTerm.toLowerCase();
    return companies.filter(c =>
      c.company_name?.toLowerCase().includes(q) ||
      c.company_slug?.toLowerCase().includes(q)
    );
  }, [companies, searchTerm]);

  const enabled  = useMemo(() => filtered.filter(c => c.is_enabled === 1), [filtered]);
  const disabled = useMemo(() => filtered.filter(c => c.is_enabled !== 1), [filtered]);

  // ── Total extensions actives ──
  const totalExtensions = useMemo(() => {
    if (!isExtension) return 0;
    return enabled.reduce((s, c) => s + parseFloat(c.prix_mensuel || 0), 0);
  }, [enabled, isExtension]);

  const modalBg = t.mode === 'dark' ? '#111620' : '#ffffff';
  const bodyBg  = t.mode === 'dark' ? t.bg : '#f6f7f9';

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return ReactDOM.createPortal(
    <>
      <div
        className="fixed inset-0 flex items-center justify-center z-50 p-4"
        style={{ backgroundColor: t.bgOverlay, animation: 'fmFadeIn 0.2s ease-out' }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col border"
          style={{ backgroundColor: modalBg, borderColor: t.border, boxShadow: t.shadowLg, animation: 'fmSlideUp 0.25s ease-out' }}
        >

          {/* ── HEADER ── */}
          <div className="px-6 py-4 flex items-start justify-between flex-shrink-0 border-b"
            style={{ borderColor: t.border }}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <h2 className="text-[16px] font-bold truncate" style={{ color: t.text }}>
                  {feature.feature_label || feature.feature_name}
                </h2>
                {/* Badge extension */}
                {isExtension && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0"
                    style={{ backgroundColor: t.warningText + '18', color: t.warningText }}>
                    <Zap size={10} />
                    EXTENSION PAYANTE
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <code className="text-[11px] font-mono px-2 py-0.5 rounded"
                  style={{ backgroundColor: t.primarySoft, color: t.primaryText }}>
                  {feature.feature_name}
                </code>
                <span className="text-[11px] font-medium" style={{ color: t.textMuted }}>
                  {companies.length} entreprise{companies.length !== 1 ? 's' : ''}
                </span>
                {/* Prix défaut si extension */}
                {isExtension && feature.prix_defaut > 0 && (
                  <span className="text-[11px] font-bold px-2 py-0.5 rounded"
                    style={{ backgroundColor: t.warningText + '12', color: t.warningText }}>
                    Référence : {Number(feature.prix_defaut).toLocaleString('fr-FR')} FCFA/mois
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg transition-colors flex-shrink-0 ml-4"
              style={{ color: t.textMuted }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = t.primarySofter}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
              <X size={18} />
            </button>
          </div>

          {/* ── TOTAL EXTENSIONS ── */}
          {isExtension && enabled.length > 0 && (
            <div className="px-6 py-3 border-b flex items-center justify-between flex-shrink-0"
              style={{ borderColor: t.border, backgroundColor: t.warningText + '08' }}>
              <span className="text-[12px] font-semibold" style={{ color: t.textSecondary }}>
                {enabled.length} entreprise{enabled.length > 1 ? 's' : ''} abonnée{enabled.length > 1 ? 's' : ''}
              </span>
              <span className="text-[13px] font-bold" style={{ color: t.warningText }}>
                Total : {totalExtensions.toLocaleString('fr-FR')} FCFA/mois
              </span>
            </div>
          )}

          {/* ── SEARCH ── */}
          <div className="px-6 py-3 border-b flex-shrink-0" style={{ borderColor: t.border }}>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2" size={16} style={{ color: t.textMuted }} />
              <input
                type="text"
                placeholder="Rechercher une entreprise…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm font-medium border outline-none transition-all duration-200"
                style={{ backgroundColor: t.bgInput, borderColor: t.border, color: t.text }}
                onFocus={(e) => { e.target.style.backgroundColor = t.bgInputFocus; e.target.style.borderColor = t.borderActive; e.target.style.boxShadow = `0 0 0 3px ${t.primarySoft}`; }}
                onBlur={(e) => { e.target.style.backgroundColor = t.bgInput; e.target.style.borderColor = t.border; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          </div>

          {/* ── BODY ── */}
          <div className="flex-1 overflow-y-auto px-6 py-5" style={{ backgroundColor: bodyBg }}>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="relative w-10 h-10 mb-4">
                  <div className="absolute inset-0 rounded-full border-2 border-t-transparent animate-spin"
                    style={{ borderColor: t.border, borderTopColor: t.primary }} />
                </div>
                <span className="text-sm font-medium" style={{ color: t.textMuted }}>Chargement…</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <Building2 size={36} className="mx-auto mb-3" style={{ color: t.textMuted, opacity: 0.4 }} />
                <p className="text-sm font-medium" style={{ color: t.textSecondary }}>Aucune entreprise trouvée</p>
              </div>
            ) : (
              <div className="space-y-5">
                {enabled.length > 0 && (
                  <div>
                    <GroupHeader icon={CheckCircle2} label="Activées" count={enabled.length} color={t.successText} t={t} />
                    <div className="space-y-2">
                      {enabled.map(c => (
                        <EnabledRow key={c.id} company={c} feature={feature} t={t}
                          processing={processing} prixSaving={prixSaving}
                          onToggle={handleToggle} onSavePrice={handleSavePrice} />
                      ))}
                    </div>
                  </div>
                )}
                {disabled.length > 0 && (
                  <div>
                    <GroupHeader icon={XCircle} label="Désactivées" count={disabled.length} color={t.textMuted} t={t} />
                    <div className="space-y-2">
                      {disabled.map(c => (
                        <DisabledRow key={c.id} company={c} feature={feature} t={t}
                          processing={processing} onToggle={handleToggle} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal prix activation extension */}
      {prixModal && (
        <PrixActivationModal
          company={prixModal}
          feature={feature}
          onConfirm={handleConfirmExtension}
          onCancel={() => setPrixModal(null)}
          t={t}
        />
      )}

      <style>{`
        @keyframes fmFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes fmSlideUp {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>,
    document.body
  );
};

export default FeatureManageModal;